import {
    Alert,
    Button,
    ColorPickerInput,
    Headline,
    IconButton,
    InputControl,
    Picker,
    StyleFunction,
    Text,
    TextInput,
    useForm,
    useMerchantConfig,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    baseButtonColor,
    ButtonDimensions,
    CmpEventProductCalendar,
    formatAmount,
    ILayoutProduct,
    LayoutButtonMetaData,
    MultiProductButton,
    ValidationSchema,
} from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useMemo, useState } from "react";
import { FlatList, View } from "react-native";
import { Divider } from "react-native-paper";
import { useAdminSession } from "../../../../../../session";
import { ButtonSizeForm } from "../../../../button-size-form";
import { MultiProductModalOutput } from "..";
import { schemaButtonDimensions } from "../validation";
import { LayoutReferenceData } from "../../../product-layout-operation-reducer";

type MultiProductButtonInput = Pick<
    MultiProductButton,
    "color" | "label" | "productIds" | "cmpEventProductIds"
>;

const schemaMultiProductButton: ValidationSchema<MultiProductButtonInput> = {
    label: { required: true },
    color: { required: true, color: true },
};

export function MultiProductForm({
    metaData,
    values,
    products,
    events,
    onSubmit,
}: {
    values: MultiProductButton;
    metaData: LayoutButtonMetaData;
    products: LayoutReferenceData["products"];
    events: LayoutReferenceData["events"];
    onSubmit: (values?: MultiProductModalOutput) => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);

    const dimensionForm = useForm<ButtonDimensions>(schemaButtonDimensions, {
        width: values.width || 1,
        height: values.height || 1,
    });

    const form = useForm<MultiProductButtonInput>(schemaMultiProductButton, {
        color: values.color || "",
        label: values.label || "",
        productIds: values.productIds || [],
        cmpEventProductIds: values.cmpEventProductIds || [],
    });

    const [{ values: formValues, errors }, { setValue, handleSubmit }] = form;
    const [{ values: dimensionFormValues }] = dimensionForm;

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [selectedProductId, setSelectedProductId] = useState<string>("");
    const [selectedProductQuantity, setSelectedProductQuantity] =
        useState<string>("1");

    const [selectedCmpEventId, setSelectedCmpEventId] = useState<string>("");
    const [selectedCmpProductId, setSelectedCmpProductId] =
        useState<string>("");
    const [selectedCmpQuantity, setSelectedCmpQuantity] = useState<string>("1");

    const productsById = useMemo(
        () => new Map(products.map(product => [product.id, product])),
        [products]
    );

    const eventsByExternalId = useMemo(
        () => new Map(events.map(event => [event.externalId, event])),
        [events]
    );

    const cmpEventProductsByEventAndProductId = useMemo(() => {
        const map = new Map<
            string,
            Map<string, (typeof events)[0]["products"][0]>
        >();

        events.forEach(event => {
            map.set(
                event.externalId,
                new Map(
                    event.products.map(product => [product.externalId, product])
                )
            );
        });

        return map;
    }, [events]);

    const productPickerItems = useMemo(
        () =>
            products.map(item => (
                <Picker.Item
                    label={item.name}
                    value={item.id}
                    key={`multi_product_${item.id}`}
                />
            )),
        [products]
    );

    const cmpEventPickerItems = useMemo(
        () =>
            events.map(item => (
                <Picker.Item
                    label={
                        item.active === false
                            ? `${item.name} (${t(
                                  "common.inactive",
                                  "Not active"
                              )})`
                            : item.name
                    }
                    value={item.externalId}
                    key={`multi_cmp_event_${item.id}`}
                />
            )),
        [events, t]
    );

    const selectedCmpEvent = eventsByExternalId.get(selectedCmpEventId);

    const cmpProductPickerItems = useMemo(
        () =>
            (selectedCmpEvent?.products || []).map(item => (
                <Picker.Item
                    label={item.name}
                    value={item.externalId}
                    key={`multi_cmp_product_${item.id}`}
                />
            )),
        [selectedCmpEvent?.products]
    );

    const addProductToSelection = useCallback(() => {
        if (!selectedProductId) {
            return;
        }

        const quantity = parseQuantity(selectedProductQuantity);
        const updatedItems = [...formValues.productIds];
        const existingIndex = formValues.productIds.findIndex(
            item => item.id === selectedProductId
        );

        if (existingIndex === -1) {
            updatedItems.push({ id: selectedProductId, quantity });
        } else {
            updatedItems[existingIndex] = {
                ...updatedItems[existingIndex],
                quantity,
            };
        }

        setValue("productIds", updatedItems);
        setSelectedProductId("");
        setSelectedProductQuantity("1");
        setErrorMessage(null);
    }, [
        formValues.productIds,
        selectedProductId,
        selectedProductQuantity,
        setValue,
    ]);

    const addCmpEventProductToSelection = useCallback(() => {
        if (!selectedCmpEventId || !selectedCmpProductId) {
            return;
        }

        const quantity = parseQuantity(selectedCmpQuantity);

        const updatedItems = [...formValues.cmpEventProductIds];
        const existingIndex = formValues.cmpEventProductIds.findIndex(
            item =>
                item.eventId === selectedCmpEventId &&
                item.productId === selectedCmpProductId
        );

        if (existingIndex === -1) {
            updatedItems.push({
                eventId: selectedCmpEventId,
                productId: selectedCmpProductId,
                quantity,
            });
        } else {
            updatedItems[existingIndex] = {
                ...updatedItems[existingIndex],
                quantity,
            };
        }

        setValue("cmpEventProductIds", updatedItems);
        setSelectedCmpProductId("");
        setSelectedCmpQuantity("1");
        setErrorMessage(null);
    }, [
        formValues.cmpEventProductIds,
        selectedCmpEventId,
        selectedCmpProductId,
        selectedCmpQuantity,
        setValue,
    ]);

    const handleUpdateProductQuantity = useCallback(
        (item: { id: string }, newQuantity: string) => {
            const quantity = parseQuantity(newQuantity);

            const updatedItems = [...formValues.productIds];
            const existingIndex = formValues.productIds.findIndex(
                itemItr => itemItr.id === item.id
            );

            if (existingIndex === -1) {
                updatedItems.push({ id: item.id, quantity });
            } else {
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    quantity,
                };
            }

            setValue("productIds", updatedItems);
        },
        [formValues.productIds, setValue]
    );

    const handleUpdateEventProductQuantity = useCallback(
        (item: { eventId: string; productId: string }, newQuantity: string) => {
            const quantity = parseQuantity(newQuantity);

            const updatedItems = [...formValues.cmpEventProductIds];
            const existingIndex = formValues.cmpEventProductIds.findIndex(
                itemItr =>
                    itemItr.eventId === item.eventId &&
                    itemItr.productId === item.productId
            );

            if (existingIndex === -1) {
                updatedItems.push({
                    eventId: item.eventId,
                    productId: item.productId,
                    quantity,
                });
            } else {
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    quantity,
                };
            }

            setValue("cmpEventProductIds", updatedItems);
        },
        [formValues.cmpEventProductIds, setValue]
    );

    const handleRemoveProductId = useCallback(
        (item: { id: string }) => {
            const updatedItems = formValues.productIds.filter(
                previousItem => previousItem.id !== item.id
            );
            setValue("productIds", updatedItems);
        },
        [formValues, setValue]
    );

    const handleRemoveEventProductId = useCallback(
        item => {
            const updatedItems = formValues.cmpEventProductIds.filter(
                previousItem =>
                    !(
                        previousItem.eventId === item.eventId &&
                        previousItem.productId === item.productId
                    )
            );

            setValue("cmpEventProductIds", updatedItems);
        },
        [formValues, setValue]
    );

    const localValidation = useCallback(() => {
        if (
            formValues.productIds.length === 0 &&
            formValues.cmpEventProductIds.length === 0
        ) {
            setErrorMessage(
                t(
                    "layout.multi_product_button.error_no_products",
                    "Add at least one product to the button."
                )
            );
            return;
        }

        const mappedProducts = formValues.productIds
            .map(item => productsById.get(item.id))
            .filter(Boolean) as ILayoutProduct[];

        const productAmount = formValues.productIds.reduce((sum, item) => {
            const foundProduct = productsById.get(item.id);

            if (!foundProduct) {
                return sum;
            }

            return sum + foundProduct.amount * item.quantity;
        }, 0);

        const cmpAmount = formValues.cmpEventProductIds.reduce((sum, item) => {
            const foundProduct = cmpEventProductsByEventAndProductId
                .get(item.eventId)
                ?.get(item.productId);

            if (!foundProduct) {
                return sum;
            }

            if (foundProduct.calendar) {
                const calendar = JSON.parse(
                    foundProduct.calendar
                ) as CmpEventProductCalendar;

                return sum + (calendar.dates[0]?.amount || 0) * item.quantity;
            }

            return sum + (foundProduct.amount || 0) * item.quantity;
        }, 0);

        const defaultLabel =
            formValues.label ||
            (mappedProducts.length === 1 &&
            formValues.cmpEventProductIds.length === 0
                ? mappedProducts[0].buttonText || mappedProducts[0].name
                : t(
                      "backoffice.layout.button_type_MULTI_PRODUCT",
                      "Multi product"
                  ));

        setErrorMessage(null);

        onSubmit({
            buttonType: "MULTI_PRODUCT",
            formValues: {
                ...values,
                ...dimensionFormValues,
                ...formValues,
            },
            displayValues: {
                label: formValues.label || defaultLabel,
                color: formValues.color || baseButtonColor,
                formattedAmount: formatAmount(
                    productAmount + cmpAmount,
                    currency,
                    {
                        locale: locale as AvailableLocale,
                    }
                ),
            },
        });
    }, [
        currency,
        cmpEventProductsByEventAndProductId,
        dimensionFormValues,
        formValues,
        locale,
        onSubmit,
        productsById,
        t,
        values,
    ]);

    return (
        <>
            <ButtonSizeForm metaData={metaData} form={dimensionForm} />

            <View style={styles.rowContainer}>
                <InputControl
                    description={t(
                        "layout.multi_product_button.label_description",
                        "If this field is left blank, a default label is used."
                    )}
                    style={styles.rowContent}
                    error={errors.label}
                >
                    <TextInput
                        label={t("layout.button.label", "The button's text")}
                        placeholder={t(
                            "layout.multi_product_button.label_placeholder",
                            "Write a short button text"
                        )}
                        value={formValues.label}
                        onChangeText={text => setValue("label", text)}
                        onClear={() => setValue("label", "")}
                    />
                </InputControl>

                <InputControl error={errors.color} style={styles.rowContent}>
                    <ColorPickerInput
                        defaultColor={formValues.color || undefined}
                        label={t("layout.button.color", "Button color")}
                        onChangeColor={newColor => setValue("color", newColor)}
                    />
                </InputControl>
            </View>

            <Divider />

            <Headline size="h4" style={styles.sectionHeadline}>
                {t(
                    "layout.multi_product_button.products_header",
                    "POS products"
                )}
            </Headline>
            <View style={styles.selectionRow}>
                <View style={styles.quantityInput}>
                    <TextInput
                        keyboardType="numeric"
                        label={t("layout.multi_product_button.qty", "Qty")}
                        value={selectedProductQuantity}
                        onChangeText={setSelectedProductQuantity}
                    />
                </View>
                <View style={styles.rowContent}>
                    <Picker
                        label={t(
                            "layout.multi_product_button.product",
                            "Product"
                        )}
                        selectedValue={selectedProductId}
                        onValueChange={setSelectedProductId}
                    >
                        <Picker.Item
                            label={t(
                                "layout.multi_product_button.pick_product",
                                "Pick a product"
                            )}
                            value=""
                            key="product_none"
                        />
                        {productPickerItems}
                    </Picker>
                </View>
                <Button
                    onPress={addProductToSelection}
                    size="small"
                    disabled={!selectedProductId}
                    style={styles.addButton}
                >
                    {t("common.add", "Add")}
                </Button>
            </View>

            {formValues.productIds.length > 0 ? (
                <FlatList
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    data={formValues.productIds}
                    keyExtractor={item => `selected_pos_${item.id}`}
                    renderItem={({ item, index: productIndex }) => {
                        const foundProduct = productsById.get(item.id);

                        return (
                            <View
                                style={[
                                    styles.itemRow,
                                    productIndex % 2 === 0
                                        ? styles.oddRow
                                        : null,
                                ]}
                            >
                                <View style={styles.itemQuantity}>
                                    <TextInput
                                        keyboardType="numeric"
                                        label={t(
                                            "layout.multi_product_button.qty",
                                            "Qty"
                                        )}
                                        value={String(item.quantity)}
                                        onChangeText={text =>
                                            handleUpdateProductQuantity(
                                                item,
                                                text
                                            )
                                        }
                                    />
                                </View>
                                <Text style={styles.itemLabel}>
                                    {foundProduct?.name || item.id}
                                </Text>
                                <IconButton
                                    name="delete"
                                    size="small"
                                    onPress={() => handleRemoveProductId(item)}
                                    color="black"
                                />
                            </View>
                        );
                    }}
                />
            ) : null}

            <Divider />

            <Headline size="h4" style={styles.sectionHeadline}>
                {t(
                    "layout.multi_product_button.cmp_products_header",
                    "CMP event products"
                )}
            </Headline>
            <View style={styles.selectionRow}>
                <View style={styles.quantityInput}>
                    <TextInput
                        keyboardType="numeric"
                        label={t("layout.multi_product_button.qty", "Qty")}
                        value={selectedCmpQuantity}
                        onChangeText={setSelectedCmpQuantity}
                    />
                </View>
                <View style={styles.pickerHolder}>
                    <View style={styles.rowContent}>
                        <Picker
                            label={t(
                                "layout.multi_product_button.event",
                                "Event"
                            )}
                            selectedValue={selectedCmpEventId}
                            onValueChange={newValue => {
                                setSelectedCmpEventId(newValue);
                                setSelectedCmpProductId("");
                            }}
                        >
                            <Picker.Item
                                label={t(
                                    "layout.multi_product_button.pick_event",
                                    "Pick an event"
                                )}
                                value=""
                                key="cmp_event_none"
                            />
                            {cmpEventPickerItems}
                        </Picker>
                    </View>
                    <View style={styles.rowContent}>
                        <Picker
                            label={t(
                                "layout.multi_product_button.product",
                                "Product"
                            )}
                            selectedValue={selectedCmpProductId}
                            disabled={!selectedCmpEventId}
                            onValueChange={setSelectedCmpProductId}
                        >
                            <Picker.Item
                                label={t(
                                    "layout.multi_product_button.pick_product",
                                    "Pick a product"
                                )}
                                value=""
                                key="cmp_product_none"
                            />
                            {cmpProductPickerItems}
                        </Picker>
                    </View>
                </View>
                <Button
                    onPress={addCmpEventProductToSelection}
                    size="small"
                    disabled={!selectedCmpEventId || !selectedCmpProductId}
                    style={styles.addButton}
                >
                    {t("common.add", "Add")}
                </Button>
            </View>

            {formValues.cmpEventProductIds.length > 0 ? (
                <FlatList
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    data={formValues.cmpEventProductIds}
                    keyExtractor={item =>
                        `selected_cmp_${item.eventId}_${item.productId}`
                    }
                    renderItem={({ item, index: cmpIndex }) => {
                        const foundEvent = eventsByExternalId.get(item.eventId);
                        const foundProduct = cmpEventProductsByEventAndProductId
                            .get(item.eventId)
                            ?.get(item.productId);

                        return (
                            <View
                                style={[
                                    styles.itemRow,
                                    cmpIndex % 2 === 0 ? styles.oddRow : null,
                                ]}
                            >
                                <View style={styles.itemQuantity}>
                                    <TextInput
                                        keyboardType="numeric"
                                        label={t(
                                            "layout.multi_product_button.qty",
                                            "Qty"
                                        )}
                                        value={String(item.quantity)}
                                        onChangeText={text =>
                                            handleUpdateEventProductQuantity(
                                                item,
                                                text
                                            )
                                        }
                                    />
                                </View>
                                <Text style={styles.itemLabel}>
                                    {(foundEvent?.name || item.eventId) +
                                        " / " +
                                        (foundProduct?.name || item.productId)}
                                </Text>
                                <IconButton
                                    name="delete"
                                    size="small"
                                    onPress={() =>
                                        handleRemoveEventProductId(item)
                                    }
                                    color="black"
                                />
                            </View>
                        );
                    }}
                />
            ) : null}

            {errorMessage ? <Alert type="warning">{errorMessage}</Alert> : null}

            <Button type="primary" onPress={handleSubmit(localValidation)}>
                {t("common.save", "Save")}
            </Button>
        </>
    );
}

function parseQuantity(value: string): number {
    const parsedQuantity = parseInt(value, 10);

    if (Number.isNaN(parsedQuantity) || parsedQuantity < 1) {
        return 1;
    }

    return parsedQuantity;
}

const styleFunc: StyleFunction = theme => ({
    rowContent: {
        ...theme.styles.flex1,
    },
    rowContainer: {
        ...theme.styles.row,
        columnGap: theme.spacingScale * 2,
    },

    selectionRow: {
        ...theme.styles.row,
        // ...theme.styles.flex1,
        gap: theme.spacingScale,
        alignItems: "center",
    },
    quantityInput: {
        width: 75,
    },
    itemRow: {
        ...theme.styles.row,
        gap: theme.spacingScale,
        alignItems: "center",
    },
    itemLabel: {
        ...theme.styles.flex1,
        paddingTop: theme.spacingScale,
    },
    itemQuantity: {
        width: 75,
    },

    scrollView: {
        ...theme.styles.flex1,
        minHeight: 80,
        maxHeight: 240,
    },

    scrollContent: {
        marginRight: theme.spacingScale,
    },

    oddRow: {
        ...theme.styles.oddRow,
    },

    addButton: {
        marginTop: theme.spacingScale,
    },

    sectionHeadline: {
        marginBottom: 0,
    },

    pickerHolder: { flexDirection: "row", flex: 1, gap: theme.spacingScale },
});
